/** Add one task. The other half of entry is the bulk grid on /new; this is for the
 *  thing that comes up at three in the afternoon.
 *
 *  Kept deliberately close to the grid's row shape so the two screens agree on what a
 *  task needs, and on which people may hold which role.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Company, type DaySummary, type OpenedDay, type Assignee,
         type Workload } from '@/api/client'
import { ErrorNote, fmtDate, fmtDay, todayISO } from './ui'
import { SignedOffConfirm, useSignedOffGate } from './SignedOffConfirm'
import { useAuth } from '@/lib/auth'
import { CompanyPicker } from '@/components/CompanyPicker'
import { PeoplePicker, type Quorum }
  from '@/components/PeoplePicker'
import { DocumentsField, type DraftDocument } from './DocumentsField'

const PRIORITIES = ['Urgent', 'Important', 'Regular']

export function AddTaskDrawer({ date, onClose }: {
  date?: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const { me } = useAuth()
  // One possible answer to "who does it", so it is answered rather than asked.
  const filesForSelfOnly = !!me?.may_enter_tasks && !me?.is_owner && !me?.is_manager
  const [err, setErr] = useState<unknown>(null)
  const [saved, setSaved] = useState(0)
  const [form, setForm] = useState({
    task: '', company: '',
    responsible_ids: (filesForSelfOnly && me ? [me.user_id] : []) as string[],
    approver_ids: [] as string[],
    responsible_rule: 'ANY' as Quorum, approver_rule: 'ANY' as Quorum,
    priority: 'Regular', date: date ?? todayISO(), end_date: '', remarks: '',
    documents: [] as DraftDocument[],
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const companies = useQuery({
    queryKey: ['companies'], queryFn: () => api.get<Company[]>('/companies'),
  })
  const people = useQuery({
    // `/people`, not `/users`: scoped to the caller's own team, and without the
    // contact details. A manager is offered exactly the people the service will let
    // them file work onto, so the picker cannot suggest a choice that is then refused.
    queryKey: ['people'], queryFn: () => api.get<Assignee[]>('/people'),
  })
  // Same key as the entry grid and the rail, so this is a cache read rather than a
  // third request for the same figures.
  //
  // Only fetched for somebody allowed to read reports: `/reports/workload` needs
  // APPROVER, so for a responsible person filing their own work it answered 403 and
  // painted an error banner across a drawer that was otherwise working. The bands
  // say little about one person in any case.
  const load = useQuery({
    queryKey: ['workload'],
    queryFn: () => api.get<Workload>('/reports/workload'),
    enabled: !!me?.permissions?.reports,
  })

  // Only companies you can still file against, and only people who can actually
  // approve — the same predicates the recurring editor and Master data use, so the
  // three screens cannot disagree about who is eligible.
  const active = (companies.data ?? []).filter((c) => c.active)
  const staff = (people.data ?? []).filter((p) => p.active)
  const approvers = staff.filter((p) => p.roles.includes('APPROVER') || p.is_owner)
  // Somebody with entry but no team files their own work and nobody else's, so the
  // doer is not a choice - `/people` sends them their approvers as well, and offering
  // those as doers would suggest something the service refuses.
  const doers = filesForSelfOnly
    ? staff.filter((p) => p.user_id === me?.user_id)
    : staff

  // A company's default approver is captured in Master data and, until now, read by
  // nothing. Filling it in is the point of having recorded it — visibly, and still
  // overridable, because silently attaching an approver the Owner did not choose is
  // worse than leaving it blank.
  //
  // A setter rather than an effect. As an effect keyed on `companies.data` and
  // `people.data` it re-ran whenever either query re-resolved — on a refetch, a window
  // focus, an invalidation from anywhere — and overwrote an approver the Owner had
  // just picked by hand. Suggesting on the one action that should suggest, choosing a
  // company, cannot do that.
  // The company's default approver, but only where it is a choice that can be saved.
  //
  // The default is usually a partner. For a manager that is somebody outside their
  // team, and for a person filing their own work it may not be on their approver
  // ladder at all - either way the service refuses it, so pre-filling would offer a
  // choice that cannot be saved. The rule below picks the first of these that holds:
  //
  //   the only approver on offer, which is exactly the case where the Owner has named
  //   one person; then the company default if it is among those offered; then blank.
  const setCompany = (name: string) => {
    const co = active.find((c) => c.name === name)
    const offered = approvers.map((p) => p.user_id)
    const preferred =
      offered.length === 1 ? offered
      : (co?.default_approver_user_id && offered.includes(co.default_approver_user_id)
          ? [co.default_approver_user_id] : [])
    setForm((f) => ({ ...f, company: name, approver_ids: preferred }))
  }

  // Whether the day being filed against is one the firm works. The drawer lets you
  // pick any date, so it can land a task on a holiday just as the bulk grid can.
  const dayInfo = useQuery({
    queryKey: ['day', form.date],
    queryFn: () => api.get<DaySummary>(`/days/${form.date}`),
    enabled: !!form.date,
  })
  const holiday = dayInfo.data ? !dayInfo.data.is_working_day : false

  // Armed, not applied: the day opens on the first task actually filed, so changing
  // your mind and closing the drawer leaves the calendar alone.
  const [armed, setArmed] = useState(false)
  const [opened, setOpened] = useState<OpenedDay | null>(null)
  useEffect(() => { setArmed(false); setOpened(null) }, [form.date])

  const save = useMutation({
    mutationFn: (acknowledge: string[] = []) =>
      // `end_date` is `''` while the field is empty, and the API takes a date or
      // null - an empty string is neither and would come back a 422 on every
      // ordinary task filed from this form.
      api.post('/tasks', { ...form, end_date: form.end_date || null,
                           acknowledge_closed: acknowledge }),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['day'] })
      if (armed && holiday && !opened) {
        setOpened(await api.post<OpenedDay>(`/days/${form.date}/open`, {}))
        setArmed(false)
        qc.invalidateQueries({ queryKey: ['calendar'] })
        qc.invalidateQueries({ queryKey: ['holidays'] })
      }
    },
    onError: setErr,
  })

  const ready = !!form.task.trim() && !!form.company
    && form.responsible_ids.length > 0

  // A person who has signed off for the day is a legitimate choice, so the server
  // asks rather than refusing and this replays the same request once answered.
  const gate = useSignedOffGate((ack) => save.mutateAsync(ack))
  // Which button was pressed, remembered across the question. "Add another" and
  // "Add and close" do different things afterwards, and the answer arrives from the
  // dialog rather than from either button.
  const [again, setAgain] = useState(false)

  const afterFiling = (addAnother: boolean) => {
    if (!addAnother) return onClose()
    // Tasks arrive in runs per client, so keep the company and the date and clear the
    // rest — retyping the client for every one of its six filings is the tax this
    // avoids.
    setSaved((n) => n + 1)
    setForm((f) => ({ ...f, task: '', remarks: '', priority: 'Regular',
                      documents: [] }))
  }

  const submit = async (addAnother: boolean) => {
    setErr(null)
    setAgain(addAnother)
    // `undefined` means a question is outstanding, not that anything failed - so
    // nothing happens yet, and the dialog finishes the job.
    if (await gate.attempt() === undefined) return
    afterFiling(addAnother)
  }

  return (
    <>
      {gate.asking && (
        <SignedOffConfirm
          people={gate.asking} busy={save.isPending}
          onCancel={gate.cancel}
          onConfirm={async () => {
            if (await gate.confirm() !== undefined) afterFiling(again)
          }} />
      )}
      <div className="fixed inset-0 bg-ink/10 z-10" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] bg-surface border-l border-rule
                        shadow-2xl z-20 flex flex-col">
        <div className="px-5 py-3.5 border-b border-rule flex items-center">
          <h3 className="text-base font-semibold tracking-tight">Add a task</h3>
          {saved > 0 && (
            <span className="ml-2.5 text-[11.5px] text-done-ink">
              {saved} added
            </span>
          )}
          <span className="flex-1" />
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-lg px-1">×</button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-3.5">
          {err ? <ErrorNote error={err} /> : null}

          <div>
            <label className="label">What needs doing</label>
            <textarea className="w-full rounded-md border border-rule px-2.5 py-2 text-[13px]"
                      rows={2} maxLength={2000} value={form.task} autoFocus
                      placeholder="File GSTR-3B for August 2026"
                      onChange={(e) => set('task', e.target.value)} />
          </div>

          <div>
            <label className="label">Company</label>
            {/* Inline rather than in a popover, for the reason given at
                "Who approves it" below: the drawer has the room the grid cell does
                not, and a popover inside a drawer is one layer too many.

                Searchable, because it sat directly above a field that could be typed
                at while this one could only be scrolled - which is what a firm with
                forty clients noticed first. */}
            <CompanyPicker companies={active}
                           value={form.company ? [form.company] : []}
                           onChange={(ns) => setCompany(ns[0] ?? '')}
                           onDone={() => undefined} />
            <p className="text-[11.5px] text-ink-3 mt-1">
              The same task for several companies? Do it on{' '}
              <b>Write the day's list</b> — it makes one row per company, each with
              that company's own approver, so you can check them before filing.
            </p>
          </div>

          <div>
            <label className="label">Who does it</label>
            <PeoplePicker staff={doers} load={load.data} kind="doer"
                          value={form.responsible_ids}
                          rule={form.responsible_rule}
                          onChange={(ids, rule) => setForm((f) => ({
                            ...f, responsible_ids: ids, responsible_rule: rule }))}
                          onDone={() => undefined} />
          </div>

          <div>
            <label className="label">Who approves it</label>
            {/* Inline rather than in a popover: the drawer has the room the grid cell
                does not, and a popover inside a drawer is one layer too many. */}
            <PeoplePicker staff={approvers} load={load.data} kind="approver"
                          value={form.approver_ids} rule={form.approver_rule}
                          onChange={(ids, rule) => setForm((f) => ({
                            ...f, approver_ids: ids, approver_rule: rule }))}
                          onDone={() => undefined} />
            <p className="text-[11.5px] text-ink-3 mt-1">
              {filesForSelfOnly
                ? `Choose one — your own work cannot be filed without somebody to
                   review it. Who you may pick is set by an owner.`
                : `Filled in from the company's default approver where it has one.
                   Leaving it blank means the task completes without review.`}
            </p>
          </div>

          <div className="flex gap-2.5">
            <div className="flex-1">
              <label className="label">Priority</label>
              <select className="field" value={form.priority}
                      onChange={(e) => set('priority', e.target.value)}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="label">Date</label>
              {/* No back-dating: a day whose carry-forward has already run is a day
                  where nothing will act on the task. */}
              <input className="field" type="date" value={form.date} min={todayISO()}
                     onChange={(e) => set('date', e.target.value)} />
            </div>
            <div className="flex-1">
              <label className="label">
                Until <span className="text-ink-3 font-normal">optional</span>
              </label>
              {/* Leave it blank for an ordinary task, which is what this form filed
                  before the field existed. Set it and the task runs from Date to
                  here: on the list every day between, and not carried forward until
                  the window closes. `min` is the start, not today - an end before
                  its own beginning is the one shape that cannot mean anything, and
                  the server refuses it in the same words. */}
              <input className="field" type="date" value={form.end_date}
                     min={form.date || todayISO()}
                     onChange={(e) => set('end_date', e.target.value)} />
            </div>
          </div>

          {form.end_date && form.end_date >= form.date && (
            <p className="text-[12.3px] text-ink-3">
              On the list every day from <b>{fmtDay(form.date)}</b> to{' '}
              <b>{fmtDay(form.end_date)}</b>, and due on the last of them. It is not
              carried forward or chased as late while the window is open.
            </p>
          )}

          {opened ? (
            <div className="rounded-md bg-done-tint px-3 py-2.5 text-[12px]
                            text-done-ink">
              <b>{fmtDate(opened.day)} is now a working day.</b>{' '}
              {opened.will_carry_in
                ? 'Tonight\u2019s carry-forward will bring outstanding work onto it.'
                : opened.moved.length
                  ? `${opened.moved.length} outstanding task${
                      opened.moved.length === 1 ? '' : 's'} moved onto it.`
                  : 'There was no outstanding work to bring onto it.'}
            </div>
          ) : holiday ? (
            <div className="rounded-md bg-pend-tint px-3 py-2.5 text-[12px]
                            text-pend-ink">
              {armed ? (
                <>
                  <b>{fmtDate(form.date)} will become a working day</b> when you add
                  this task. Outstanding work comes onto it, and it sends its digest
                  and reminders like any other day.{' '}
                  <button className="underline" onClick={() => setArmed(false)}>
                    Leave it a holiday
                  </button>
                </>
              ) : (
                <>
                  <b>{fmtDate(form.date)} is not a working day.</b> A task dated here
                  is never moved on and never chased — unless the firm is working it.
                  {' '}
                  {/* Owner only: opening the day is `POST /days/{day}/open`, which
                      they alone may call. The warning itself stays for everybody,
                      because the consequence is theirs either way. */}
                  {me?.is_owner && (
                    <button className="underline" onClick={() => setArmed(true)}>
                      Work this day
                    </button>
                  )}
                </>
              )}
            </div>
          ) : null}

          <div>
            <label className="label">Documents it must arrive with — optional</label>
            {/* Same field as the entry grid, so the two ways of filing a task cannot
                disagree about what a document list is. */}
            <DocumentsField value={form.documents}
                            onChange={(docs) => set('documents', docs)} />
          </div>

          <div>
            <label className="label">Opening remark — optional</label>
            <input className="field" value={form.remarks} maxLength={1000}
                   onChange={(e) => set('remarks', e.target.value)} />
          </div>
        </div>

        <div className="px-5 py-3.5 border-t border-rule flex gap-2">
          <button className="btn btn-primary" disabled={!ready || save.isPending}
                  onClick={() => submit(false)}>
            {save.isPending ? 'Adding…' : 'Add task'}
          </button>
          <button className="btn" disabled={!ready || save.isPending}
                  onClick={() => submit(true)}>
            Add and another
          </button>
          <span className="flex-1" />
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </aside>
    </>
  )
}
